/**
 * Enhanced BankID Sign-In Component
 * Clean, modern interface for BankID authentication
 */

import React, { useState, useEffect } from 'react';
import { useBankID, BankIDState } from '../hooks/useBankID';
import { bankidAppUrl } from '../utils/bankidLaunch';
import { Shield, Smartphone, QrCode, CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import HyriaLogo from '../assets/hyria-logo.svg';

interface BankIDSignInProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export const BankIDSignIn: React.FC<BankIDSignInProps> = ({ onSuccess, onError }) => {
  const {
    state,
    message,
    error,
    qrImageUrl,
    autoStartToken,
    initiateBankID,
    cancelBankID,
    isMobile,
    isLoading,
    isAuthenticated
  } = useBankID();

  const [qrError, setQrError] = useState(false);

  // Handle authentication success
  useEffect(() => {
    if (isAuthenticated && onSuccess) {
      onSuccess();
    }
  }, [isAuthenticated, onSuccess]);

  // Handle errors
  useEffect(() => {
    if (error && onError) {
      onError(error);
    }
  }, [error, onError]);

  // Render QR code with error handling
  const renderQRCode = () => {
    if (!qrImageUrl || isMobile) return null;

    return (
      <div className="flex flex-col items-center space-y-4">
        <div className="relative">
          {qrError ? (
            <div className="w-64 h-64 bg-gray-100 rounded-lg flex flex-col items-center justify-center">
              <AlertCircle className="w-12 h-12 text-gray-400 mb-2" />
              <p className="text-sm text-gray-600">QR-kod kunde inte laddas</p>
              <button
                onClick={() => {
                  setQrError(false);
                  initiateBankID();
                }}
                className="mt-2 text-sm text-[#62B8E9] hover:underline"
              >
                Försök igen
              </button>
            </div>
          ) : (
            <>
              <img
                src={`${qrImageUrl}?t=${Date.now()}`}
                alt="BankID QR Code"
                className="w-64 h-64 rounded-lg shadow-lg"
                onError={() => setQrError(true)}
              />
              <div className="absolute -bottom-2 -right-2 bg-white rounded-full p-1 shadow-md">
                <QrCode className="w-6 h-6 text-[#62B8E9]" />
              </div>
            </>
          )}
        </div>
        <p className="text-sm text-gray-600 text-center">
          Skanna QR-koden med BankID-appen på din mobil
        </p>
      </div>
    );
  };

  // Render mobile instructions
  const renderMobileInstructions = () => {
    if (!isMobile || !autoStartToken) return null;

    return (
      <div className="flex flex-col items-center space-y-4">
        <div className="w-20 h-20 bg-[#62B8E9]/10 rounded-full flex items-center justify-center">
          <Smartphone className="w-10 h-10 text-[#62B8E9]" />
        </div>
        <div className="text-center space-y-2">
          <p className="text-gray-700 font-medium">
            Tryck på knappen för att öppna BankID-appen
          </p>
          <a
            href={bankidAppUrl(autoStartToken, window.location.origin, navigator.userAgent)}
            className="inline-block mt-2 px-6 py-3 bg-[#62B8E9] text-white rounded-lg hover:bg-[#4A9FD1] transition-colors font-medium"
          >
            Öppna BankID
          </a>
          <p className="text-sm text-gray-600">
            Kom tillbaka hit när du är klar
          </p>
        </div>
      </div>
    );
  };

  // Render state-specific content
  const renderStateContent = () => {
    switch (state) {
      case BankIDState.IDLE:
        return (
          <div className="flex flex-col items-center space-y-6">
            <div className="w-24 h-24 bg-[#62B8E9]/10 rounded-full flex items-center justify-center">
              <Shield className="w-12 h-12 text-[#62B8E9]" />
            </div>
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold text-gray-900">
                Logga in med BankID
              </h2>
              <p className="text-gray-600">
                Säker autentisering för svenska bostadsrättsägare
              </p>
            </div>
            <button
              onClick={initiateBankID}
              className="px-6 py-3 bg-[#62B8E9] text-white rounded-lg hover:bg-[#4A9FD1] transition-colors font-medium flex items-center space-x-2"
            >
              <Shield className="w-5 h-5" />
              <span>Logga in med BankID</span>
            </button>
          </div>
        );

      case BankIDState.INITIALIZING:
        return (
          <div className="flex flex-col items-center space-y-4">
            <Loader2 className="w-12 h-12 text-[#62B8E9] animate-spin" />
            <p className="text-gray-700 font-medium">Startar BankID...</p>
          </div>
        );

      case BankIDState.WAITING_FOR_USER:
      case BankIDState.USER_SIGNING:
        return (
          <div className="flex flex-col items-center space-y-6">
            {isMobile ? renderMobileInstructions() : renderQRCode()}
            <div className="text-center space-y-2">
              <div className="flex items-center justify-center space-x-2">
                <Loader2 className="w-5 h-5 text-[#62B8E9] animate-spin" />
                <p className="text-gray-700 font-medium">{message}</p>
              </div>
              <p className="text-sm text-gray-500">
                Detta kan ta upp till 30 sekunder
              </p>
            </div>
          </div>
        );

      case BankIDState.PROCESSING:
        return (
          <div className="flex flex-col items-center space-y-4">
            <Loader2 className="w-12 h-12 text-[#62B8E9] animate-spin" />
            <div className="text-center space-y-2">
              <p className="text-gray-700 font-medium">Behandlar autentisering...</p>
              <p className="text-sm text-gray-500">Vänligen vänta</p>
            </div>
          </div>
        );

      case BankIDState.SUCCESS:
        return (
          <div className="flex flex-col items-center space-y-4">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <div className="text-center space-y-2">
              <p className="text-xl font-bold text-gray-900">Inloggning lyckades!</p>
              <p className="text-gray-600">Du dirigeras vidare...</p>
            </div>
          </div>
        );

      case BankIDState.ERROR:
      case BankIDState.TIMEOUT:
      case BankIDState.CANCELLED:
        return (
          <div className="flex flex-col items-center space-y-4">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <div className="text-center space-y-2">
              <p className="text-xl font-bold text-gray-900">Autentisering misslyckades</p>
              <p className="text-red-600">{error || 'Ett oväntat fel uppstod'}</p>
            </div>
            <button
              onClick={initiateBankID}
              className="px-6 py-3 bg-[#62B8E9] text-white rounded-lg hover:bg-[#4A9FD1] transition-colors font-medium"
            >
              Försök igen
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img src={HyriaLogo} alt="Hyria" className="h-12" />
        </div>

        {/* Main Content */}
        {renderStateContent()}

        {/* Cancel button when loading */}
        {isLoading && state !== BankIDState.INITIALIZING && (
          <div className="mt-6 text-center">
            <button
              onClick={cancelBankID}
              className="text-gray-600 hover:text-gray-800 underline text-sm"
            >
              Avbryt
            </button>
          </div>
        )}

        {/* Help text */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="text-center text-sm text-gray-500">
            <p>Har du problem med BankID?</p>
            <a
              href="https://support.bankid.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#62B8E9] hover:underline"
            >
              Få hjälp här
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Standalone BankID authentication button
 * Can be used anywhere in the app
 */
export const BankIDButton: React.FC<{
  className?: string;
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'small' | 'medium' | 'large';
}> = ({ className = '', variant = 'primary', size = 'medium' }) => {
  const { initiateBankID, isLoading, state } = useBankID();

  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#62B8E9]';

  const variantClasses = {
    primary: 'bg-[#62B8E9] text-white hover:bg-[#4A9FD1] shadow-md hover:shadow-lg',
    secondary: 'bg-gray-100 text-gray-900 hover:bg-gray-200',
    outline: 'border-2 border-[#62B8E9] text-[#62B8E9] hover:bg-[#62B8E9] hover:text-white'
  };

  const sizeClasses = {
    small: 'px-3 py-1.5 text-sm space-x-1.5',
    medium: 'px-4 py-2 text-base space-x-2',
    large: 'px-6 py-3 text-lg space-x-2.5'
  };

  const iconSizes = {
    small: 'w-4 h-4',
    medium: 'w-5 h-5',
    large: 'w-6 h-6'
  };

  return (
    <button
      onClick={initiateBankID}
      disabled={isLoading}
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className} ${
        isLoading ? 'opacity-50 cursor-not-allowed' : ''
      }`}
    >
      {isLoading && state === BankIDState.INITIALIZING ? (
        <Loader2 className={`${iconSizes[size]} animate-spin`} />
      ) : (
        <Shield className={iconSizes[size]} />
      )}
      <span>{isLoading ? 'Väntar på BankID...' : 'Logga in med BankID'}</span>
    </button>
  );
};

export default BankIDSignIn;